import React, { useState } from 'react';
import { useCountry } from '@/contexts/CountryContext';
import RomanianExperiencesSection from './romania/RomanianExperiencesSection';
import { ArrowRight, Clock } from 'lucide-react';

const AlbaniaExperiencesSection: React.FC = () => {
  const { experiences, label } = useCountry();
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);


  return (
    <section id="experiences" className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961] block mb-4">
            Signature Collection
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6 gold-underline">
            {label === 'Romania' ? 'Romanian Experiences' : 'Albanian Experiences'}
          </h2>
          <p className="font-sans text-sm text-[#777] max-w-lg mx-auto leading-relaxed mt-10">
            Beyond accommodation, we craft immersive journeys that reveal the soul of {label}: its landscapes, mountains, heritage, and flavors.

          </p>
        </div>


        {/* Experiences Grid - Alternating layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {experiences.map((exp, index) => (
            <div
              key={exp.id}
              className="group relative cursor-pointer"
              onMouseEnter={() => setHoveredId(exp.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => setExpandedId(expandedId === exp.id ? null : exp.id)}
            >
              {/* Image */}
              <div className="img-zoom relative aspect-[3/4] overflow-hidden">
                <img
                  src={exp.image}
                  alt={exp.alt || `${exp.title}, ${exp.subtitle}, luxury Albania travel experience`}

                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

                {/* Content Overlay */}
                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                  {/* Duration badge */}
                  <div className="flex items-center gap-1.5 mb-3">
                    <Clock className="w-3 h-3 text-[#C9A961]" />
                    <span className="font-sans text-[10px] tracking-[0.2em] uppercase text-white/70">
                      {exp.duration}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="font-serif text-2xl md:text-3xl text-white mb-1">
                    {exp.title}
                  </h3>
                  <p className="font-sans text-[11px] tracking-[0.15em] uppercase text-[#C9A961] mb-3">
                    {exp.subtitle}
                  </p>

                  {/* Description - slides up on hover */}
                  <div
                    className={`transition-all duration-500 overflow-hidden ${
                      hoveredId === exp.id || expandedId === exp.id
                        ? 'max-h-40 opacity-100'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <p className="font-sans text-xs text-white/80 leading-relaxed mb-4">
                      {exp.description}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const el = document.getElementById('plan-journey');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="font-sans text-[10px] tracking-[0.2em] uppercase text-white flex items-center gap-2 hover:text-[#C9A961] transition-colors"
                    >
                      Inquire Now <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Index number */}
                <div className="absolute top-6 right-6">
                  <span className="font-serif text-4xl text-white/20 font-light">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Country-aware wrapper: Romania renders the immersive 10-experience
 * "Romanian Experiences" section (mosaic grid + Learn More deep links),
 * while Albania keeps its original signature collection untouched.
 */
const ExperiencesSection: React.FC = () => {
  const { country } = useCountry();
  if (country === 'romania') return <RomanianExperiencesSection />;
  return <AlbaniaExperiencesSection />;
};

export default ExperiencesSection;
